/**
 * The `applicants:profile_view` event fires on Universal Applicant View open,
 * applicant changes, and entry or re-entry into full page for the same applicant.
 * Re-renders, tab changes, and Exit full page do not fire it; closing resets
 * deduplication. Full-page views use actor sampling at
 * FULL_PAGE_PROFILE_VIEW_SAMPLE_RATE (10%); 90% of actors send none. Drawer views
 * are unsampled. Raw event counts are not profile-open counts.
 * The `source` property resolves the path a recruiter takes to the profile.
 *
 * The capture lives here rather than at its ApplicantDrawerPopup call site
 * because that container is `.js`, and `tsconfig.json` sets `allowJs` without
 * `checkJs`. A literal event name written there is never compared against the
 * catalog in `utils/analytics.ts`; written here it is.
 *
 * The UAV opens via a `?applicantId=…` query param on top of whichever list the
 * recruiter was on, so the source is derived from the current route + query and
 * from whether this open is a prev/next navigation within an already-open UAV.
 *
 * Pure and route-based on purpose (unit tested). Finer attribution
 * (notification, bulk_drilldown) would require the opener to pass an explicit
 * source and can be layered on later.
 */
import type { PostHogProxy } from 'containers/PostHogProvider';
import type { NavigationMethod } from 'containers/ApplicantDrawerPopup/resumeReview/ResumeReviewContext';
import {
  FULL_PAGE_PROFILE_VIEW_SAMPLE_RATE,
  isActorSampled,
} from './analyticsSampling';

export type ProfileViewSource =
  | 'uav_pagination'
  | 'applicant_search'
  | 'applicant_list'
  | 'pipeline_board'
  | 'direct_link';

export interface ProfileViewSourceInput {
  // True when the UAV was already open and moved to another applicant (prev/next).
  isPagination: boolean;
  // location.search of the current route.
  search: string;
  // location.pathname of the current route.
  pathname: string;
  // Current account slug (first path segment), to match the master applicants view.
  accountSlug?: string;
}

const PIPELINE_STAGES_PATH = /\/jobs\/[^/]+\/v2\/stages/;

export function resolveProfileViewSource({
  isPagination,
  search,
  pathname,
  accountSlug,
}: ProfileViewSourceInput): ProfileViewSource {
  if (isPagination) {
    return 'uav_pagination';
  }

  // A search wins over the list it was run from, so a searched master list and
  // a browsed one are different sources. It does not win over a pipeline route:
  // the monolith's DashboardsController redirects a legacy opening dashboard to
  // a `/jobs/…/v2/stages/…` URL carrying both `applicantId` and `query`, and
  // that open belongs to the board it lands on, not to the cross-opening search
  // surface.
  const params = new URLSearchParams(search);
  if (params.get('query') && !PIPELINE_STAGES_PATH.test(pathname)) {
    return 'applicant_search';
  }

  if (accountSlug && pathname === `/${accountSlug}/applicants`) {
    return 'applicant_list';
  }

  if (PIPELINE_STAGES_PATH.test(pathname)) {
    return 'pipeline_board';
  }

  return 'direct_link';
}

export interface ProfileViewInput extends ProfileViewSourceInput {
  applicantId: string;
  viewMode?: 'drawer' | 'full_page';
  navigationMethod?: NavigationMethod | null;
  reviewSessionId?: string | null;
  actorId?: string;
}

export function captureProfileView(
  posthog: PostHogProxy,
  {
    applicantId,
    viewMode,
    navigationMethod,
    reviewSessionId,
    actorId,
    ...sourceInput
  }: ProfileViewInput,
): void {
  if (
    viewMode === 'full_page' &&
    !isActorSampled(actorId, FULL_PAGE_PROFILE_VIEW_SAMPLE_RATE)
  )
    return;
  posthog.capture('applicants:profile_view', {
    applicant_id: applicantId,
    source: resolveProfileViewSource(sourceInput),
    ...(viewMode && { view_mode: viewMode }),
    ...(navigationMethod && { navigation_method: navigationMethod }),
    ...(viewMode === 'full_page' && {
      review_session_id: reviewSessionId,
      sample_rate: FULL_PAGE_PROFILE_VIEW_SAMPLE_RATE,
    }),
  });
}
